import { useState } from "react"
import { Button, Form, Spinner, Alert } from "react-bootstrap"
import http from "../helpers/Request"
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { formatDateTime } from "../helpers/BlogText"

const MySwal = withReactContent(Swal)

function EventRegistrationForm({ event, onSuccess, onCancel, accessToken = null }) {
  const [form, setForm] = useState({
    fullname: '',
    mobile_phone: '',
    business_email: '',
    company_organization: '',
    job_title: '',
    referral_name: '',
    referral_company_organization: ''
  })
  const [loading, setLoading] = useState(false)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [formErrors, setFormErrors] = useState({})

  // Real-time email uniqueness check
  const checkEmailUniqueness = async (email) => {
    if (!email || !isValidEmail(email)) {
      setEmailError('')
      return
    }

    try {
      setCheckingEmail(true)
      const params = new URLSearchParams({ email })
      if (accessToken) {
        params.append('event_access_token', accessToken)
      }

      const response = await http.get(`/api/events/${event.id}/check-email?${params.toString()}`)
      
      if (response.data.status && response.data.exists) {
        setEmailError('Email ini sudah terdaftar untuk event ini')
      } else {
        setEmailError('')
      }
    } catch (error) {
      console.error('Error checking email:', error)
      // Don't show error to user for email checking failure
    } finally {
      setCheckingEmail(false)
    }
  }

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Indonesian phone validation function
  const isValidIndonesianPhone = (phone) => {
    if (!phone) return true // Optional field
    const phoneRegex = /^(\+62|62|0)[0-9]{8,12}$/
    return phoneRegex.test(phone.replace(/\s+/g, ''))
  }

  // Form validation
  const validateForm = () => {
    const errors = {}

    if (!form.fullname.trim()) {
      errors.fullname = 'Full name is required'
    } else if (form.fullname.trim().length > 100) {
      errors.fullname = 'Full name must not exceed 100 characters'
    }

    if (!form.business_email.trim()) {
      errors.business_email = 'Business email is required'
    } else if (!isValidEmail(form.business_email)) {
      errors.business_email = 'Please enter a valid email address'
    }

    if (!form.company_organization.trim()) {
      errors.company_organization = 'Company/Organization is required'
    } else if (form.company_organization.trim().length > 150) {
      errors.company_organization = 'Company/Organization must not exceed 150 characters'
    }

    if (!form.job_title.trim()) {
      errors.job_title = 'Job title is required'
    } else if (form.job_title.trim().length > 100) {
      errors.job_title = 'Job title must not exceed 100 characters'
    }

    if (form.mobile_phone && !isValidIndonesianPhone(form.mobile_phone)) {
      errors.mobile_phone = 'Please enter a valid Indonesian phone number'
    }

    if (form.referral_name.trim().length > 100) {
      errors.referral_name = 'Referral name must not exceed 100 characters'
    }

    if (form.referral_company_organization.trim().length > 150) {
      errors.referral_company_organization = 'Referral company/organization must not exceed 150 characters'
    }

    if (emailError) {
      errors.business_email = emailError
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear specific field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Check email uniqueness when email changes
    if (name === 'business_email') {
      setEmailError('')
      if (value && isValidEmail(value)) {
        // Debounce email checking
        clearTimeout(window.emailCheckTimeout)
        window.emailCheckTimeout = setTimeout(() => {
          checkEmailUniqueness(value)
        }, 500)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (checkingEmail) {
      MySwal.fire({
        title: 'Please wait',
        text: 'Checking email availability...',
        icon: 'info'
      })
      return
    }

    try {
      setLoading(true)
      
      const registrationData = {
        fullname: form.fullname.trim(),
        mobile_phone: form.mobile_phone.trim() || null,
        business_email: form.business_email.trim(),
        company_organization: form.company_organization.trim(),
        job_title: form.job_title.trim(),
        referral_name: form.referral_name.trim() || null,
        referral_company_organization: form.referral_company_organization.trim() || null
      }

      const response = await http.post(`/api/events/${event.id}/register`, {
        ...registrationData,
        event_access_token: accessToken,
      })

      if (response.data.status) {
        MySwal.fire({
          title: 'Registration Successful!',
          html: `
            <p>Thank you for registering for <strong>${event.title}</strong>!</p>
            <br/>
            <p>A confirmation email has been sent to <strong>${form.business_email}</strong></p>
          `,
          icon: 'success',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#1037C6'
        }).then(() => {
          onSuccess()
        })

        // Reset form
        setForm({
          fullname: '',
          mobile_phone: '',
          business_email: '',
          company_organization: '',
          job_title: '',
          referral_name: '',
          referral_company_organization: ''
        })
      }
    } catch (error) {
      console.error('Registration error:', error)
      
      let errorMessage = 'Registration failed. Please try again.'
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      MySwal.fire({
        title: 'Registration Failed',
        text: errorMessage,
        icon: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="event-registration-form">
      {/* Event Info */}
      <div className="mb-4 p-3 bg-light rounded">
        <h5 className="mb-4 text-brand">{event.title}</h5>
        <div className="mb-1">
          <i className="fas fa-calendar-alt"></i>
          <strong>Date:</strong> {formatDateTime(event.event_date)}
        </div>
        {event.event_type === 'offline' && event.event_location && (
          <div className="mb-1">
            <i className="fas fa-map-marker-alt"></i>
            <strong>Location:</strong> {event.event_location}
          </div>
        )}
        {event.event_type === 'online' && (
          <div className="mb-1">
            <i className="fas fa-video"></i>
            <strong>Type:</strong> Online Event
          </div>
        )}
      </div>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="fullname"
            value={form.fullname}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.fullname}
            placeholder="Enter your full name"
            maxLength={100}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.fullname}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Business Email <span className="text-danger">*</span></Form.Label>
          <div className="position-relative">
            <Form.Control
              type="email"
              name="business_email"
              value={form.business_email}
              onChange={handleInputChange}
              disabled={loading}
              isInvalid={!!formErrors.business_email}
              placeholder="Enter your business email"
            />
            {checkingEmail && (
              <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                <Spinner size="sm" animation="border" />
              </div>
            )}
          </div>
          <Form.Control.Feedback type="invalid">
            {formErrors.business_email}
          </Form.Control.Feedback>
          {emailError && (
            <Alert variant="warning" className="mt-2 py-1">
              <small>{emailError}</small>
            </Alert>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Mobile Phone</Form.Label>
          <Form.Control
            type="tel"
            name="mobile_phone"
            value={form.mobile_phone}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.mobile_phone}
            placeholder="e.g., +6281234567890 or 081234567890"
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.mobile_phone}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Indonesian phone number format (optional)
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Company/Organization <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="company_organization"
            value={form.company_organization}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.company_organization}
            placeholder="Enter your company or organization"
            maxLength={150}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.company_organization}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Job Title <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="job_title"
            value={form.job_title}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.job_title}
            placeholder="Enter your job title"
            maxLength={100}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.job_title}
          </Form.Control.Feedback>
        </Form.Group>
        <strong>How did you know about this event?</strong>
        <Form.Group className="mb-3">
          <Form.Label>Referral Name</Form.Label>
          <Form.Control
            type="text"
            name="referral_name"
            value={form.referral_name}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.referral_name}
            placeholder="Enter name"
            maxLength={100}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.referral_name}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Referral Company/Organization</Form.Label>
          <Form.Control
            type="text"
            name="referral_company_organization"
            value={form.referral_company_organization}
            onChange={handleInputChange}
            disabled={loading}
            isInvalid={!!formErrors.referral_company_organization}
            placeholder="Enter company or organization"
            maxLength={150}
          />
          <Form.Control.Feedback type="invalid">
            {formErrors.referral_company_organization}
          </Form.Control.Feedback>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="brand" 
            type="submit" 
            disabled={loading || checkingEmail || !!emailError}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" animation="border" />
                Registering...
              </>
            ) : (
              'Register Now'
            )}
          </Button>
        </div>
      </Form>

      <style jsx>{`
        .text-brand {
          color: #007bff;
        }
        
        .event-registration-form .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
    </div>
  )
}

export default EventRegistrationForm
